import { ConnectionKey } from '../../../common/hooks/use-home-api.hook';
import { AgentId } from './agents';

/**
 * Ready-made agents: jobs a furnishing or flooring business actually wants done.
 *
 * An empty box asking "what should your team do?" is the hardest question on
 * the page. These answer it with work that already fits the team — each names
 * the specialists who do it (so the vendor sees who will work) and what has to
 * be connected for it to find anything. Picking one puts its brief in the
 * composer, where it can be adapted before it is sent: a template is a start,
 * not a button that spends credits on its own.
 *
 * The words live in i18n (AGENTS.TEMPLATES.ITEM.<ID>.*) so the brief reaches
 * the lead in the vendor's language.
 */

export type TemplateCategory = 'monitoring' | 'leads' | 'content' | 'catalogue' | 'reporting';

export const TEMPLATE_CATEGORIES: readonly TemplateCategory[] = ['monitoring', 'leads', 'content', 'catalogue', 'reporting'];

export interface AgentTemplate {
    readonly id: string;
    readonly category: TemplateCategory;
    /** Who does the work, first is the one it is mostly about. */
    readonly agents: readonly AgentId[];
    /** What must be connected for it to find anything. */
    readonly needs?: readonly ConnectionKey[];
}

export const AGENT_TEMPLATES: readonly AgentTemplate[] = [
    { id: 'morning_recap', category: 'monitoring', agents: ['analyst', 'sales'] },
    { id: 'competitor_watch', category: 'monitoring', agents: ['market'], needs: ['website', 'competitors'] },
    { id: 'funnel_leak', category: 'monitoring', agents: ['analyst'], needs: ['visualizer'] },
    { id: 'hot_leads', category: 'leads', agents: ['sales'] },
    { id: 'shopper_questions', category: 'leads', agents: ['sales'] },
    { id: 'push_products', category: 'leads', agents: ['analyst', 'catalogue'] },
    { id: 'plan_posts', category: 'content', agents: ['marketing', 'catalogue'], needs: ['social'] },
    { id: 'content_gaps', category: 'content', agents: ['marketing', 'analyst'] },
    { id: 'lead_follow_up_email', category: 'content', agents: ['marketing', 'sales'], needs: ['email'] },
    { id: 'catalogue_health', category: 'catalogue', agents: ['catalogue'] },
    { id: 'organise_categories', category: 'catalogue', agents: ['catalogue'] },
    { id: 'ads_review', category: 'reporting', agents: ['ads', 'analyst'], needs: ['ads'] },
    { id: 'monthly_review', category: 'reporting', agents: ['analyst', 'market', 'sales'] },
];

/** Templates in a category, or all of them. */
export const templatesIn = (category: TemplateCategory | 'all'): AgentTemplate[] =>
    AGENT_TEMPLATES.filter((t) => category === 'all' || t.category === category);

/**
 * What a template still needs, given what is connected.
 *
 * Only `missing` counts — a connection that needs attention is still there,
 * and a locked one is a plan question the card should not nag about.
 */
export const missingFor = (
    template: AgentTemplate,
    connections: ReadonlyArray<{ key: ConnectionKey; state: string }> | null,
): ConnectionKey[] => {
    if (!connections || !template.needs?.length) return [];
    return template.needs.filter((key) => connections.find((c) => c.key === key)?.state === 'missing');
};

export const templateKey = (id: string) => `AGENTS.TEMPLATES.ITEM.${id.toUpperCase()}`;
